import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { StorageUtilService } from 'src/app/core/services/storage-util.service';
import { Pipeline } from '../interfaces/pipeline.model';
import { PipelineApiService } from './pipeline-api.service';

const ACTIVE_PIPELINE_STORAGE_KEY = 'kanban-active-pipeline-id';

@Injectable({
  providedIn: 'root',
})
export class PipelineService {
  private pipelinesSub = new BehaviorSubject<Pipeline[]>([]);
  public pipelines$ = this.pipelinesSub.asObservable();

  constructor(
    private storage: StorageUtilService<any>,
    private pipelineApi: PipelineApiService,
  ) {}

  public async refreshPipelines(language: string): Promise<Pipeline[]> {
    const pipelines = await firstValueFrom(
      this.pipelineApi.findByUser(language),
    );
    this.pipelinesSub.next(pipelines);
    return pipelines;
  }

  public async savePipeline(pipeline: Pipeline): Promise<Pipeline> {
    const current = this.pipelinesSub.getValue();
    const exists = current.some((p) => p.id === pipeline.id);

    let saved: Pipeline;
    if (exists) {
      saved = await firstValueFrom(
        this.pipelineApi.update(pipeline.id, pipeline),
      );
      const updated = current.map((p) => (p.id === saved.id ? saved : p));
      this.pipelinesSub.next(updated);
    } else {
      saved = await firstValueFrom(this.pipelineApi.create(pipeline));
      this.pipelinesSub.next([...current, saved]);
    }
    return saved;
  }

  public async duplicatePipeline(pipeline: Pipeline): Promise<Pipeline> {
    const duplicated = await firstValueFrom(this.pipelineApi.duplicate(pipeline));
    const current = this.pipelinesSub.getValue();
    this.pipelinesSub.next([...current, duplicated]);
    return duplicated;
  }

  public loadPipelines(): Pipeline[] {
    return this.pipelinesSub.getValue();
  }

  public async deletePipeline(id: string): Promise<void> {
    await firstValueFrom(this.pipelineApi.delete(id));
    const pipelines = this.pipelinesSub
      .getValue()
      .filter((p) => p.id !== id);
    this.pipelinesSub.next(pipelines);

    if (this.getActivePipelineId() === id) {
      this.storage.removeItem(ACTIVE_PIPELINE_STORAGE_KEY);
    }
  }

  public getPipelineById(id: string): Pipeline | undefined {
    return this.pipelinesSub.getValue().find((p) => p.id === id);
  }

  public async syncShares(
    pipelineId: string,
    newUserIds: number[],
    originalUserIds: number[],
  ): Promise<void> {
    const toAdd = newUserIds.filter((id) => !originalUserIds.includes(id));
    const toRemove = originalUserIds.filter((id) => !newUserIds.includes(id));

    if (toAdd.length) {
      await firstValueFrom(this.pipelineApi.share(pipelineId, toAdd, 'view'));
    }

    for (const uid of toRemove) {
      await firstValueFrom(this.pipelineApi.removeShare(pipelineId, uid));
    }
  }

  public setActivePipelineId(id: string): void {
    this.storage.setItem(ACTIVE_PIPELINE_STORAGE_KEY, id);
  }

  public getActivePipelineId(): string | null {
    return this.storage.getItem(ACTIVE_PIPELINE_STORAGE_KEY) || null;
  }
}
